import { AnyType, MULTI_VALUE } from '@thehubz/shared/model';

export interface GroupedMergeResult<T> {
  mergedData: T[];
  noMergeData: NoMergeData[];
}

export interface NoMergeData {
  key: string;
  occurrences: number;
  values?: unknown[];
}

export interface MergeOptions {
  sortKey?: string; // If provided, will sort objects by this key if we have an array of objects
  arrayDeepCompare?: boolean; // If true, will deeply compare arrays of objects
  arraySimpleMerge?: boolean; // If true and array is of objects, will not merge but return the array with MULTI_VALUE
}

export class ObjectBatchUtil<T extends object, O extends object> {
  private readonly sourceData: T[] | T[][]; // Allow sourceData to be an array of arrays

  constructor(data: T[] | T[][]) {
    // create a deep copy of the data
    this.sourceData = JSON.parse(JSON.stringify(data));
  }

  static sortArraysByFirstValue(data: AnyType[]): AnyType[] {
    data.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (Array.isArray(item[key])) {
          item[key] = ObjectBatchUtil.sortObjectArrayByFirstValue(item[key] as AnyType[]);
        }
      });
    });
    return data;
  }

  static sortObjectArrayByFirstValue(array: AnyType[]) {
    return array.sort((a, b) => {
      const aValue = Object.values(a)[0];
      const bValue = Object.values(b)[0];
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        // sort alphabetically
        return aValue.localeCompare(bValue);
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        // sort numerically
        return aValue - bValue;
      }
      // if the first value is undefined or null, we do not sort
      return 0;
    });
  }

  static removeMultiValueFromArrays(data: AnyType): AnyType {
    Object.keys(data).forEach((key) => {
      if (Array.isArray(data[key])) {
        data[key] = data[key].filter((item: AnyType) => item !== MULTI_VALUE);
      }
    });
    return data;
  }

  /*
   * [
  [
    {
      buyoutRightsTitle: 'creators',
      whatever: 'sososo',
    },
    {
      buyoutRightsTitle: 'cast',
      whatever: 'sososo',
    },
    {
      buyoutRightsTitle: 'xyz',
      whatever: 'sososo',
    },
  ],
  [
    {
      buyoutRightsTitle: 'creators',
      whatever: 'dododo',
    },
    {
      buyoutRightsTitle: 'cast',
      whatever: 'dododo',
    }
  ]
]
   * What we need is, to have kind of equality key, thats the hierarchy of properties to consider equal e.g. title.sublevel.name and then compare those objects.
   */
  mergeGroupBy(groupKey: string, innerSort?: { arrayKey: string; propertyKey: string }): GroupedMergeResult<O> {
    // this will only work on array of arrays.
    if (!Array.isArray(this.sourceData[0])) {
      throw new Error('sourceData must be an array of arrays');
    }

    const groupMap = new Map<string, T[]>();
    (this.sourceData as T[][]).forEach((innerArray) => {
      innerArray.forEach((obj) => {
        const groupValue = (obj as Record<string, unknown>)[groupKey] as string;
        if (!groupMap.has(groupValue)) {
          groupMap.set(groupValue, []);
        }
        if (innerSort) {
          // sort the inner array on property arrayKey by the propertyKey
          const sortedInnerArray = ((obj as Record<string, unknown>)[innerSort.arrayKey] as AnyType[]).sort((a, b) => {
            const aKey = (a as Record<string, unknown>)[innerSort.propertyKey] as string | number;
            const bKey = (b as Record<string, unknown>)[innerSort.propertyKey] as string | number;
            if (aKey < bKey) return -1;
            if (aKey > bKey) return 1;
            return 0;
          });
          (obj as Record<string, unknown>)[innerSort.arrayKey] = sortedInnerArray;
        }
        groupMap.get(groupValue)?.push(obj);
      });
    });

    const result: GroupedMergeResult<O> = {
      mergedData: [],
      noMergeData: [],
    };

    // we can only keep values from groupMap that have the same length as sourceData. The others are stored separately.
    groupMap.forEach((value, key) => {
      if (value.length !== (this.sourceData as T[][]).length) {
        result.noMergeData.push({
          key,
          occurrences: value.length,
          values: value,
        });
        groupMap.delete(key);
      }
    });

    // now we merge each value in the groupMap
    groupMap.forEach((value) => {
      const mergedValue = this.mergeInternal(value, { arrayDeepCompare: true });
      result.mergedData.push(mergedValue);
    });

    return result;
  }

  merge(options?: MergeOptions): O {
    return this.mergeInternal(this.sourceData, options);
  }

  private mergeInternal(data: T[] | T[][], options: MergeOptions = {}): O {
    const isEqual = (a: unknown, b: unknown): boolean => {
      if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((val, index) => isEqual(val, b[index]));
      } else if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
        const keysA = Object.keys(a);
        const keysB = Object.keys(b);
        return keysA.length === keysB.length && keysA.every((key) => isEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]));
      }
      return a === b;
    };

    const sortArray = (array: T[]): T[] => {
      if (!options.sortKey) return array;
      return [...array].sort((a, b) => {
        const aKey = options.sortKey ? ((a as Record<string, unknown>)[options.sortKey] as string | number) : undefined;
        const bKey = options.sortKey ? ((b as Record<string, unknown>)[options.sortKey] as string | number) : undefined;
        if (aKey !== undefined && bKey !== undefined && aKey < bKey) return -1;
        if (aKey !== undefined && bKey !== undefined && aKey > bKey) return 1;
        return 0;
      });
    };

    const merge = (objects: T[] | T[][]): AnyType => {
      if (Array.isArray(objects[0])) {
        // Handle array of arrays
        const sortedArrays = (objects as T[][]).map((innerArray) => sortArray(innerArray));
        const maxLength = Math.max(...sortedArrays.map((arr) => arr.length));

        const mergedArray = [];
        for (let i = 0; i < maxLength; i++) {
          const elementsAtIndex = sortedArrays.map((arr) => arr[i]).filter((el) => el !== undefined);

          if (elementsAtIndex.length !== sortedArrays.length) {
            mergedArray.push(MULTI_VALUE as unknown as O);
          } else {
            mergedArray.push(merge(elementsAtIndex as T[]) as O);
          }
        }
        return mergedArray;
      }

      const result: Record<string, unknown> = {};
      const keys = new Set((objects as T[]).flatMap((obj) => Object.keys(obj)));

      keys.forEach((key) => {
        const values = (objects as T[]).map((obj: AnyType) => obj[key]);

        if (key === '_id') {
          // Handle _id separately
          result[key] = values;
          return;
        }

        if (values.every((value) => isEqual(value, values[0]))) {
          result[key] = values[0];
        } else if (values.some((value) => Array.isArray(value))) {
          // this is not bulletproof because array could contain different kinds of objects
          if (Array.isArray(values[0]) && typeof values[0][0] === 'object') {
            // Deeply compare arrays of objects
            if (options.arrayDeepCompare) {
              result[key] = merge(values as T[][]);
            } else if (options.arraySimpleMerge) {
              result[key] = [MULTI_VALUE];
            } else {
              const clonedObject = { ...values[0][0] };
              Object.keys(clonedObject).forEach((subKey) => {
                clonedObject[subKey] = MULTI_VALUE;
              });
              result[key] = [clonedObject];
            }
          } else {
            result[key] = [MULTI_VALUE];
          }
        } else if (values.some((value) => typeof value === 'object' && value !== null)) {
          result[key] = merge(values.filter((value) => typeof value === 'object' && value !== null) as T[]);
        } else {
          result[key] = MULTI_VALUE;
        }
      });

      return result as O;
    };

    return merge(data);
  }
}
